import { useState, type CSSProperties, type ReactNode } from "react";
import { useCustom, useGetLocale, useTranslate } from "@refinedev/core";
import { ArrowLeft, ArrowRight, BedDouble, PawPrint, Plus } from "lucide-react";
import { Button, CalendarIcon, HomeIcon, PackageIcon, SettingsTileCard, UserIcon } from "@fawkes/ui";

import "./PensionOverzichten.css";
import "./PensionWerklijst.css";
import { NieuweReserveringWizard } from "./pensionreservering/NieuweReserveringWizard";

/** The screens the boarding landing page links to — the pension werkmodus' own APPLICATIE items. */
export type PensionDashboardTarget =
  | "kennelplanning"
  | "relaties"
  | "pension-overzichten"
  | "pension-tarief";

export interface PensionDashboardProps {
  onNavigate: (page: PensionDashboardTarget) => void;
}

interface DagBezetting {
  datum: string;
  dieren: number;
  /** Pensionplaatsen van deze vestiging, of `null` zolang die niet zijn ingesteld. */
  capaciteit: number | null;
}

interface PensionDashboardData {
  vestigingId: number;
  vestigingCode: string;
  datum: string;
  kpis: {
    dierenAanwezig: number;
    aankomstenVandaag: number;
    vertrekkenVandaag: number;
    capaciteit: number | null;
    vrijePlaatsen: number | null;
  };
  bezetting: DagBezetting[];
}

/**
 * Het landingsscherm van de werkmodus Pension: hoeveel dieren logeren er, wie komt en gaat er
 * vandaag, en hoe vol staat het de komende twee weken.
 *
 * <p>Het asieldashboard is hier geen alternatief voor: dat telt opnames, verlopen entingen en
 * asielbezetting — andere dieren, andere vraag. De cijfers hier komen uit
 * {@code GET /api/pension/dashboard}, dat de pensionboekingen van de actieve vestiging telt.
 */
export function PensionDashboard({ onNavigate }: PensionDashboardProps) {
  const t = useTranslate();
  const [nieuweReservering, setNieuweReservering] = useState(false);
  const locale = useGetLocale()() ?? "nl";
  const { data, isLoading, isError } = useCustom<PensionDashboardData>({
    url: "pension/dashboard",
    method: "get",
  });
  const d = data?.data;

  return (
    <div className="pension-dashboard">
      {/* De kop van Pensionoverzichten: rond icoon, grote titel, en de kerngetallen als tegels van het asiel. */}
      <div className="po pension-dashboard__kopvak">
        <header className="po-kop">
          <span className="po-kop__icoon" aria-hidden="true"><BedDouble size={26} /></span>
          <div className="po-kop__tekst">
            <h1>{t("pensionDashboard.title")}</h1>
            <p>{t("pensionDashboard.subtitle")}</p>
          </div>
          <Button onClick={() => setNieuweReservering(true)}>
            <Plus size={16} aria-hidden="true" /> {t("nieuweReservering.knopBoeking")}
          </Button>
        </header>
      </div>
      {nieuweReservering && <NieuweReserveringWizard onSluit={() => setNieuweReservering(false)} />}

      {isError ? (
        <p className="fawkes-muted pension-dashboard__note">{t("pensionDashboard.loadFailed")}</p>
      ) : (
        <>
          <div className="po pension-dashboard__tegelvak">
          <div className="da-overview__stats pension-dashboard__tegels">
            <Kpi
              icoon={<PawPrint size={20} />}
              label={t("pensionDashboard.kpi.aanwezig")}
              value={d?.kpis.dierenAanwezig}
              loading={isLoading}
              onClick={() => onNavigate("pension-overzichten")}
            />
            <Kpi
              icoon={<ArrowLeft size={20} />}
              toon="groen"
              label={t("pensionDashboard.kpi.aankomsten")}
              value={d?.kpis.aankomstenVandaag}
              loading={isLoading}
              onClick={() => onNavigate("pension-overzichten")}
            />
            <Kpi
              icoon={<ArrowRight size={20} />}
              toon="rood"
              label={t("pensionDashboard.kpi.vertrekken")}
              value={d?.kpis.vertrekkenVandaag}
              loading={isLoading}
              onClick={() => onNavigate("pension-overzichten")}
            />
            <Kpi
              icoon={<BedDouble size={20} />}
              label={t("pensionDashboard.kpi.vrij")}
              value={d?.kpis.vrijePlaatsen}
              hint={
                d && d.kpis.capaciteit === null
                  ? t("pensionDashboard.kpi.geenCapaciteit")
                  : d?.kpis.capaciteit != null
                    ? t("pensionDashboard.kpi.vanCapaciteit", { args: [d.kpis.capaciteit] })
                    : undefined
              }
              loading={isLoading}
              onClick={() => onNavigate("kennelplanning")}
            />
          </div>
          </div>

          <BezettingChart dagen={d?.bezetting ?? []} locale={locale} loading={isLoading} t={t} />
        </>
      )}

      <div className="pension-dashboard__tiles">
        <SettingsTileCard
          tileKey="pension-planning"
          icon={<CalendarIcon className="settings-tile-icon" width={16} height={16} />}
          title={t("menu.kennelplanning")}
          description={t("pensionDashboard.tile.planning.desc")}
          items={[]}
          openLabel={t("pensionDashboard.open")}
          onOpen={() => onNavigate("kennelplanning")}
        />
        <SettingsTileCard
          tileKey="pension-relatie"
          icon={<UserIcon className="settings-tile-icon" width={16} height={16} />}
          title={t("menu.pensionRelatie")}
          description={t("pensionDashboard.tile.relatie.desc")}
          items={[]}
          openLabel={t("pensionDashboard.open")}
          onOpen={() => onNavigate("relaties")}
        />
        <SettingsTileCard
          tileKey="pension-overzichten"
          icon={<HomeIcon className="settings-tile-icon" width={16} height={16} />}
          title={t("menu.pensionOverzichten")}
          description={t("pensionDashboard.tile.overzichten.desc")}
          items={[]}
          openLabel={t("pensionDashboard.open")}
          onOpen={() => onNavigate("pension-overzichten")}
        />
        <SettingsTileCard
          tileKey="pension-tarief"
          icon={<PackageIcon className="settings-tile-icon" width={16} height={16} />}
          title={t("menu.pensionTarief")}
          description={t("pensionDashboard.tile.tarief.desc")}
          items={[]}
          openLabel={t("pensionDashboard.open")}
          onOpen={() => onNavigate("pension-tarief")}
        />
      </div>
    </div>
  );
}

/**
 * Eén getal met zijn onderschrift, als tegel van het dierenoverzicht van het asiel; aanklikken opent het overzicht
 * erachter. Een streepje bij een onbekende waarde — nooit een verzonnen nul.
 */
function Kpi({
  icoon,
  toon,
  label,
  value,
  hint,
  loading,
  onClick,
}: {
  icoon: ReactNode;
  toon?: "groen" | "rood";
  label: string;
  value: number | null | undefined;
  hint?: string;
  loading: boolean;
  onClick: () => void;
}) {
  return (
    <button type="button" className="da-overview__stat" data-toon={toon} onClick={onClick}>
      <span className="da-overview__stat-icon" aria-hidden="true">{icoon}</span>
      <span className="da-overview__stat-body">
        <span className="da-overview__stat-value">{loading || value == null ? "—" : value}</span>
        <span className="da-overview__stat-label">{label}</span>
        {hint && <span className="pension-kpi__hint">{hint}</span>}
      </span>
    </button>
  );
}

// Vaste tekenhoogte voor de staven, plus wat ruimte erboven voor de enkele cijfers die we bijzetten.
const STAAF_VLAK = 132;
const CIJFER_RUIMTE = 18;

/**
 * De bezetting per dag als kolommen, met de capaciteit als streeplijn erdoorheen.
 *
 * <p>Eén reeks, dus geen legenda: de kop zegt al wat er staat. Niet elke kolom krijgt een getal —
 * alleen vandaag en de dagen die vol of over zijn, want dat zijn de dagen waar je iets mee moet. De
 * rest leest af tegen de streeplijn, en hangt met een tooltip aan elke kolom.
 */
function BezettingChart({
  dagen,
  locale,
  loading,
  t,
}: {
  dagen: DagBezetting[];
  locale: string;
  loading: boolean;
  t: ReturnType<typeof useTranslate>;
}) {
  const capaciteit = dagen[0]?.capaciteit ?? null;
  const hoogsteDag = dagen.reduce((max, dag) => Math.max(max, dag.dieren), 0);
  // De schaal loopt tot de capaciteit óf tot de drukste dag, zodat een overboeking zichtbaar boven de
  // streeplijn uitkomt in plaats van tegen de bovenrand te blijven plakken.
  const schaal = Math.max(1, capaciteit ?? 0, hoogsteDag);
  const capaciteitY =
    capaciteit == null ? null : CIJFER_RUIMTE + STAAF_VLAK - (STAAF_VLAK * capaciteit) / schaal;

  return (
    <section className="pension-bezetting" aria-labelledby="pension-bezetting-kop">
      <h2 className="pension-bezetting__kop" id="pension-bezetting-kop">
        {t("pensionDashboard.bezetting.title")}
      </h2>
      {loading || dagen.length === 0 ? (
        <p className="fawkes-muted">{loading ? t("common.loading") : t("pensionDashboard.bezetting.leeg")}</p>
      ) : (
        <>
          <div
            className="pension-bezetting__grafiek"
            role="img"
            aria-label={t("pensionDashboard.bezetting.omschrijving", {
              args: [dagen.length, hoogsteDag],
            })}
          >
            {dagen.map((dag, index) => {
              const vol = capaciteit != null && dag.dieren >= capaciteit;
              // Een bezette dag krijgt minstens 3px, anders is "er ligt er één" niet van "leeg" te
              // onderscheiden.
              const hoogte = Math.max(
                Math.round((STAAF_VLAK * dag.dieren) / schaal),
                dag.dieren > 0 ? 3 : 0,
              );
              return (
                <div
                  className="pension-bezetting__dag"
                  key={dag.datum}
                  style={{ "--staaf-hoogte": `${hoogte}px` } as CSSProperties}
                >
                  <span className="pension-bezetting__staafvak">
                    {(index === 0 || vol) && (
                      <span className="pension-bezetting__waarde">{dag.dieren}</span>
                    )}
                    <span
                      className="pension-bezetting__staaf"
                      data-vandaag={index === 0 ? "" : undefined}
                      data-vol={vol ? "" : undefined}
                      title={`${dagLabel(dag.datum, locale)}: ${dag.dieren}`}
                    />
                  </span>
                  <span className="pension-bezetting__dagnaam">{weekdag(dag.datum, locale)}</span>
                  <span className="pension-bezetting__dagnummer">{dagnummer(dag.datum)}</span>
                </div>
              );
            })}
            {capaciteitY != null && (
              <span
                className="pension-bezetting__capaciteit"
                style={{ top: `${capaciteitY}px` }}
                aria-hidden="true"
              />
            )}
          </div>
          <p className="pension-bezetting__voet fawkes-muted">
            {capaciteit == null
              ? t("pensionDashboard.bezetting.geenCapaciteit")
              : t("pensionDashboard.bezetting.capaciteitLijn", { args: [capaciteit] })}
          </p>
        </>
      )}
    </section>
  );
}

function parse(iso: string): Date {
  // Een kale yyyy-MM-dd als UTC-middernacht lezen, zodat de weekdag niet over de tijdzonegrens schuift.
  return new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00:00Z` : iso);
}

function weekdag(iso: string, locale: string): string {
  const d = parse(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }).format(d);
}

function dagnummer(iso: string): string {
  const d = parse(iso);
  return Number.isNaN(d.getTime()) ? "" : String(d.getUTCDate());
}

function dagLabel(iso: string, locale: string): string {
  const d = parse(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(d);
}
